import React, { ReactNode } from 'react'
import { InferType } from 'yup'
import { captureException } from '@sentry/react'
import { useMutation } from '@tanstack/react-query'
import { Modal } from '../../../common/Modal'
import { ModalHeader } from '../../../common/Modal/ModalHeader'
import { ModalBody } from '../../../common/Modal/ModalBody'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { ToastType } from '../../../common/Toast/toast-type'
import { useToastNotifications } from '../../../../hooks/useToastNotification'
import { CreateUpdateEventForm } from '../CreateUpdateEventForm'
import { societyEventCreateSchema } from '../../../../forms/schemas/society_event_create'
import {
  IEventWithFiles,
  NEvents,
} from '../../../../interfaces/services/events.interfaces'
import {
  IBoardRoomEventWithFiles,
  NBoardRoomEvents,
} from '../../../../interfaces/services/boardroom-events.interfaces'
import { createEvent, updateEvent } from '../../../../api/events'
import {
  createBoardRoomEvent,
  updateBoardRoomEvent,
} from '../../../../api/boardroom-events'
import { useCurrentSociety } from '../../../../hooks/useCurrentSociety'

interface CreateUpdateEventModalProps {
  show: boolean
  close: () => void
  children?: React.ReactNode
  event?: IEventWithFiles | IBoardRoomEventWithFiles
  boardRoomEvent?: boolean
}

export const CreateUpdateEventModal = ({
  event,
  children,
  show,
  close,
  boardRoomEvent,
}: CreateUpdateEventModalProps): ReactNode => {
  const { translate } = useAppTranslation()
  const { setToastNotification } = useToastNotifications()
  const { society } = useCurrentSociety()

  const updateMode = !!event

  const showErrorMessage = (): void => {
    const message = updateMode
      ? 'createUpdateEvent.flashMessage.updateEventFailure'
      : 'createUpdateEvent.flashMessage.createEventFailure'
    setToastNotification(ToastType.DANGER, translate(message))
  }

  const showSuccessMessage = (): void => {
    const message = updateMode
      ? 'createUpdateEvent.flashMessage.updateEventSuccess'
      : 'createUpdateEvent.flashMessage.createEventSuccess'
    setToastNotification(ToastType.SUCCESS, translate(message))
  }

  const { mutate: mutateUpdateEvent, isPending: isUpdatePending } = useMutation(
    {
      mutationFn: updateEvent,
      onError: () => {
        showErrorMessage()
      },
      onSuccess: () => {
        showSuccessMessage()
        close()
      },
    }
  )
  const {
    mutate: mutateUpdateBoardRoomEvent,
    isPending: isBoardUpdatePending,
  } = useMutation({
    mutationFn: updateBoardRoomEvent,
    onError: () => {
      showErrorMessage()
    },
    onSuccess: () => {
      showSuccessMessage()
      close()
    },
  })

  const { mutate: mutateCreateEvent, isPending: isCreatePending } = useMutation(
    {
      mutationFn: createEvent,
      onError: () => {
        showErrorMessage()
      },
      onSuccess: () => {
        showSuccessMessage()
        close()
      },
    }
  )
  const { mutate: mutateCreateBoardEvent, isPending: isBoardCreatePending } =
    useMutation({
      mutationFn: createBoardRoomEvent,
      onError: () => {
        showErrorMessage()
      },
      onSuccess: () => {
        showSuccessMessage()
        close()
      },
    })

  const onSubmit = async (
    data: InferType<typeof societyEventCreateSchema>
  ): Promise<void> => {
    const _data = {
      ...data,
      documentsIds: data.documentsIds?.map((document) => document.id),
    }
    if (updateMode) {
      if (boardRoomEvent) {
        mutateUpdateBoardRoomEvent({
          id: event._id,
          body: _data as NBoardRoomEvents.NPatch.IRequestBody,
        })
      } else {
        mutateUpdateEvent({
          id: event._id,
          body: _data as NEvents.NPatch.IRequestBody,
        })
      }
    } else if (boardRoomEvent) {
      mutateCreateBoardEvent(_data as NBoardRoomEvents.NCreate.IRequestBody)
    } else {
      mutateCreateEvent(_data as NEvents.NCreate.IRequestBody)
    }
  }

  const onError = (error: unknown): void => {
    captureException(error)
    showErrorMessage()
  }

  const loading =
    isCreatePending ||
    isBoardCreatePending ||
    isUpdatePending ||
    isBoardUpdatePending

  return (
    <>
      {children}
      <Modal show={show}>
        {{
          header: (
            <ModalHeader onClose={close}>
              {translate(
                updateMode
                  ? 'createUpdateEvent.modal.editTitle'
                  : 'createUpdateEvent.modal.createTitle'
              )}
            </ModalHeader>
          ),
          body: (
            <ModalBody>
              {society && (
                <CreateUpdateEventForm
                  society={society}
                  onError={onError}
                  onSubmit={onSubmit}
                  onClose={close}
                  loading={loading}
                  event={event}
                  boardRoomEvent={boardRoomEvent}
                  documents={event?.documents}
                />
              )}
            </ModalBody>
          ),
        }}
      </Modal>
    </>
  )
}
